import React, { useState, useEffect } from 'react';
import { adminService } from '../services/adminService';
import { getRoleBadgeClasses } from '../utils/userUtils';
import LoadingSpinner from './common/LoadingSpinner';
import { toast } from 'react-toastify';
import ModalDialog from './common/ModalDialog';
import CreateUserModal from './admin/CreateUserModal';
import EditUserModal from './admin/EditUserModal';


function AdminPanel({ onClose }) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    title: '',
    description: '',
    confirmLabel: 'Confirm',
    variant: 'danger',
    onConfirm: null,
  });
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [loadingOperations, setLoadingOperations] = useState({}); // Track loading per user/operation
  const [filters, setFilters] = useState({
    role: '',
    locked: '',
    search: ''
  });

  useEffect(() => {
    loadUsers();
  }, [filters]);

  const loadUsers = async () => {
    setLoading(true);
    try {
      const data = await adminService.getUsers(filters);
      const sortedUsers = (data.users || []).sort((a, b) => {
        const nameA = (a.display_name || a.email || '').toLowerCase();
        const nameB = (b.display_name || b.email || '').toLowerCase();
        return nameA.localeCompare(nameB);
      });
      setUsers(sortedUsers);
    } catch (err) {
      toast.error(err.message || 'Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateUser = async (userData) => {
    try {
      setLoadingOperations(prev => ({ ...prev, create: true }));
      const result = await adminService.createUser(userData);
      toast.success(result.message || 'User created successfully');
      setShowCreateForm(false);
      await loadUsers();
    } catch (err) {
      toast.error(err.message || 'Failed to create user');
    } finally {
      setLoadingOperations(prev => ({ ...prev, create: false }));
    }
  };

  const handleUpdateUser = async (userId, userData) => {
    try {
      setLoadingOperations(prev => ({ ...prev, [`update_${userId}`]: true }));
      const result = await adminService.updateUser(userId, userData);
      toast.success(result.message || 'User updated successfully');
      setEditingUser(null);
      await loadUsers();
    } catch (err) {
      toast.error(err.message || 'Failed to update user');
    } finally {
      setLoadingOperations(prev => ({ ...prev, [`update_${userId}`]: false }));
    }
  };

  const openConfirmDialog = (config) => {
    const wrappedOnConfirm = config.onConfirm
      ? async () => {
        setConfirmDialog(prev => ({ ...prev, open: false }));
        await config.onConfirm();
      }
      : null;

    setConfirmDialog({
      open: true,
      title: config.title,
      description: config.description,
      confirmLabel: config.confirmLabel || 'Confirm',
      variant: config.variant || 'danger',
      onConfirm: wrappedOnConfirm,
    });
  };

  const handleLockUser = (userId) => {
    openConfirmDialog({
      title: 'Lock user',
      description: 'Are you sure you want to lock this user?',
      confirmLabel: 'Lock',
      variant: 'danger',
      loading: loadingOperations[`lock_${userId}`],
      onConfirm: async () => {
        try {
          setLoadingOperations(prev => ({ ...prev, [`lock_${userId}`]: true }));
          const result = await adminService.lockUser(userId);
          toast.success(result.message || 'User locked successfully');
          await loadUsers();
        } catch (err) {
          toast.error(err.message || 'Failed to lock user');
          throw err;
        } finally {
          setLoadingOperations(prev => ({ ...prev, [`lock_${userId}`]: false }));
        }
      },
    });
  };

  const handleUnlockUser = (userId) => {
    openConfirmDialog({
      title: 'Unlock user',
      description: 'Are you sure you want to unlock this user?',
      confirmLabel: 'Unlock',
      variant: 'default',
      loading: loadingOperations[`unlock_${userId}`],
      onConfirm: async () => {
        try {
          setLoadingOperations(prev => ({ ...prev, [`unlock_${userId}`]: true }));
          const result = await adminService.unlockUser(userId);
          toast.success(result.message || 'User unlocked successfully');
          await loadUsers();
        } catch (err) {
          toast.error(err.message || 'Failed to unlock user');
          throw err;
        } finally {
          setLoadingOperations(prev => ({ ...prev, [`unlock_${userId}`]: false }));
        }
      },
    });
  };

  const handleDeleteUser = (userId) => {
    const user = users.find(u => u.id === userId);
    const userName = user?.display_name || user?.email || 'this user';

    openConfirmDialog({
      title: 'Delete user',
      description: `Are you sure you want to delete user "${userName}"? This action cannot be undone.`,
      confirmLabel: 'Delete',
      variant: 'danger',
      loading: loadingOperations[`delete_${userId}`],
      onConfirm: async () => {
        try {
          setLoadingOperations(prev => ({ ...prev, [`delete_${userId}`]: true }));
          const result = await adminService.deleteUser(userId);
          toast.success(result.message || 'User deleted successfully');
          await loadUsers();
        } catch (err) {
          toast.error(err.message || 'Failed to delete user');
          throw err;
        } finally {
          setLoadingOperations(prev => ({ ...prev, [`delete_${userId}`]: false }));
        }
      },
    });
  };

  return (
    <div className="p-4 sm:p-6 bg-white dark:bg-slate-800 rounded-lg max-w-7xl mx-auto relative min-h-[520px]">
      {onClose && (
        <button
          onClick={onClose}
          className="absolute top-4 right-4 sm:top-6 sm:right-6 bg-transparent border-none text-2xl cursor-pointer p-2 rounded-md z-[1001] text-gray-500 dark:text-slate-400 hover:bg-gray-100 dark:hover:bg-slate-700"
        >
          ×
        </button>
      )}

      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center mb-6 pb-4 border-b-2 border-gray-200 dark:border-white/10 pr-8 sm:pr-12 gap-4">
        <h2 className="m-0 text-2xl sm:text-3xl font-bold text-gray-800 dark:text-slate-100">
          User Management
        </h2>
        <div className="flex flex-col gap-2 w-full sm:w-auto sm:flex-row sm:gap-3">
            <button
            onClick={() => setShowCreateForm(true)}
            disabled={loading || loadingOperations.create}
            className="px-4 sm:px-5 py-2 sm:py-2.5 bg-blue-500 text-white border-none rounded-md cursor-pointer font-medium text-xs sm:text-sm hover:bg-blue-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            + Create User
          </button>
        </div>
      </div>

      <div className="flex flex-col sm:flex-row gap-3 mb-5">
        <input
          type="text"
          placeholder="Search by email or name..."
          value={filters.search}
          onChange={(e) => setFilters({ ...filters, search: e.target.value })}
          className="px-3 py-2 border rounded-md text-sm w-full sm:flex-1 sm:min-w-[200px] focus:outline-none focus:ring-2 focus:ring-blue-500 border-gray-300 dark:bg-slate-900 dark:border-white/20 dark:text-slate-100 dark:placeholder-slate-500"
        />
        <div className="flex gap-3">
          <select
            value={filters.role}
            onChange={(e) => setFilters({ ...filters, role: e.target.value })}
            className="px-3 py-2 border rounded-md text-sm flex-1 min-w-[120px] sm:w-auto sm:flex-none focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-slate-900 border-gray-300 dark:border-white/20 dark:text-slate-100"
          >
            <option value="">All Roles</option>
            <option value="user">User</option>
            <option value="manager">Manager</option>
            <option value="admin">Admin</option>
          </select>
          <select
            value={filters.locked}
            onChange={(e) => setFilters({ ...filters, locked: e.target.value })}
            className="px-3 py-2 border rounded-md text-sm flex-1 min-w-[120px] sm:w-auto sm:flex-none focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-slate-900 border-gray-300 dark:border-white/20 dark:text-slate-100"
          >
            <option value="">All Status</option>
            <option value="false">Active</option>
            <option value="true">Locked</option>
          </select>
        </div>
      </div>

      {loading ? (
        <LoadingSpinner />
      ) : users.length === 0 ? (
        <div className="p-6 text-center text-gray-500 dark:text-slate-400 border border-gray-200 dark:border-white/10 rounded-lg">
          {filters.role || filters.locked || filters.search.trim() 
            ? 'No users match your filters' 
            : 'No users found'}
        </div>
      ) : (
        <>
          {/* Mobile: Card View */}
          <div className="md:hidden space-y-4 mt-2 max-h-[360px] overflow-y-auto">
            {users.map((user, index) => (
              <div key={user.id} className="bg-gray-50 dark:bg-slate-700/30 border border-gray-200 dark:border-white/10 rounded-lg p-4">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-gray-800 dark:text-slate-100 truncate mb-1">
                      {user.display_name || '-'}
                    </div>
                    <div className="text-xs text-gray-600 dark:text-slate-300 truncate mb-2">
                      {user.email || '-'}
                    </div>
                    <div className="flex flex-wrap gap-2 mb-2">
                      <span
                        className={`px-2 py-1 rounded text-xs font-medium capitalize ${getRoleBadgeClasses(user.role)}`}
                      >
                        {user.role}
                      </span>
                      {user.locked ? (
                        <span className="px-2 py-1 rounded text-xs font-semibold bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300">
                          🔒 Locked
                        </span>
                      ) : (
                        <span className="px-2 py-1 rounded text-xs font-semibold bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300">
                          ✓ Active
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-gray-500 dark:text-slate-400">
                      Created: {new Date(user.created_at).toLocaleDateString()}
                    </div>
                  </div>
                  <div className="text-xs text-gray-400 dark:text-slate-500 ml-2">#{index + 1}</div>
                </div>
                <div className="flex flex-col gap-2 pt-3 border-t border-gray-200 dark:border-white/10">
                  <button
                    onClick={() => setEditingUser(user)}
                    disabled={loadingOperations[`update_${user.id}`]}
                    className="w-full px-3 py-1.5 bg-blue-500 text-white border-none rounded cursor-pointer text-xs font-medium hover:bg-blue-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center justify-center"
                  >
                    {loadingOperations[`update_${user.id}`] ? (
                      <svg className="animate-spin h-3 w-3" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                    ) : (
                      'Edit'
                    )}
                  </button>
                  <div className="grid grid-cols-2 gap-2">
                    {user.locked ? (
                      <button
                        onClick={() => handleUnlockUser(user.id)}
                        disabled={loadingOperations[`unlock_${user.id}`] || loadingOperations[`lock_${user.id}`]}
                        className="px-3 py-1.5 bg-green-500 text-white border-none rounded cursor-pointer text-xs font-medium hover:bg-green-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center justify-center"
                      >
                        {loadingOperations[`unlock_${user.id}`] ? (
                          <svg className="animate-spin h-3 w-3" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                        ) : (
                          'Unlock'
                        )}
                      </button>
                    ) : (
                      <button
                        onClick={() => handleLockUser(user.id)}
                        disabled={loadingOperations[`lock_${user.id}`] || loadingOperations[`unlock_${user.id}`]}
                        className="px-3 py-1.5 bg-orange-500 text-white border-none rounded cursor-pointer text-xs font-medium hover:bg-orange-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center justify-center"
                      >
                        {loadingOperations[`lock_${user.id}`] ? (
                          <svg className="animate-spin h-3 w-3" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                        ) : (
                          'Lock'
                        )}
                      </button>
                    )}
                    <button
                      onClick={() => handleDeleteUser(user.id)}
                      disabled={loadingOperations[`delete_${user.id}`]}
                      className="px-3 py-1.5 bg-red-600 text-white border-none rounded cursor-pointer text-xs font-medium hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center justify-center"
                    >
                      {loadingOperations[`delete_${user.id}`] ? (
                        <svg className="animate-spin h-3 w-3" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                      ) : (
                        'Delete'
                      )}
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Desktop: Table View */}
          <div className="hidden md:block mt-2 max-h-[360px] w-full overflow-x-auto border border-gray-200 dark:border-white/10 rounded-lg">
            <div className="inline-block min-w-full align-middle">
              <table className="w-full border-collapse text-sm">
                <thead className="sticky top-0 z-10 bg-gray-50 dark:bg-slate-800">
                  <tr className="border-b-2 border-gray-200 dark:border-white/10">
                    <th className="p-3 text-left font-semibold text-gray-700 dark:text-slate-300 whitespace-nowrap">ID</th>
                    <th className="p-3 text-left font-semibold text-gray-700 dark:text-slate-300 whitespace-nowrap">Name</th>
                    <th className="p-3 text-left font-semibold text-gray-700 dark:text-slate-300 whitespace-nowrap">Email</th>
                    <th className="p-3 text-left font-semibold text-gray-700 dark:text-slate-300 whitespace-nowrap">Role</th>
                    <th className="p-3 text-left font-semibold text-gray-700 dark:text-slate-300 whitespace-nowrap">Status</th>
                    <th className="p-3 text-left font-semibold text-gray-700 dark:text-slate-300 whitespace-nowrap">Created</th>
                    <th className="p-3 text-left font-semibold text-gray-700 dark:text-slate-300 whitespace-nowrap">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((user, index) => (
                    <tr key={user.id} className="border-b border-gray-200 dark:border-white/10 hover:bg-gray-50 dark:hover:bg-slate-700/50">
                      <td className="p-3 text-gray-500 dark:text-slate-400 align-middle whitespace-nowrap">{index + 1}</td>
                      <td className="p-3 font-medium text-gray-800 dark:text-slate-100 align-middle">
                        <span className="block max-w-[9rem] truncate" title={user.display_name || '-'}>
                          {user.display_name || '-'}
                        </span>
                      </td>
                      <td className="p-3 text-gray-600 dark:text-slate-300 align-middle">
                        <span className="block max-w-[16rem] truncate" title={user.email || '-'}>
                          {user.email || '-'}
                        </span>
                      </td>
                      <td className="p-3 align-middle">
                        <span
                          className={`px-2 py-1 rounded text-xs font-medium capitalize ${getRoleBadgeClasses(user.role)}`}
                        >
                          {user.role}
                        </span>
                      </td>
                      <td className="p-3 align-middle whitespace-nowrap">
                        {user.locked ? (
                          <span className="px-2 py-1 rounded text-xs font-semibold bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300">
                            🔒 Locked
                          </span>
                        ) : (
                          <span className="px-2 py-1 rounded text-xs font-semibold bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300">
                            ✓ Active
                          </span>
                        )}
                      </td>
                      <td className="p-3 text-gray-500 dark:text-slate-400 text-xs align-middle whitespace-nowrap">
                        {new Date(user.created_at).toLocaleDateString()}
                      </td>
                      <td className="p-3 align-middle">
                        <div className="flex gap-2 flex-wrap justify-start">
                          <button
                            onClick={() => setEditingUser(user)}
                            disabled={loadingOperations[`update_${user.id}`]}
                            className="px-3 py-1.5 bg-blue-500 text-white border-none rounded cursor-pointer text-xs font-medium hover:bg-blue-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center justify-center min-w-[60px]"
                          >
                            {loadingOperations[`update_${user.id}`] ? (
                              <svg className="animate-spin h-3 w-3" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                              </svg>
                            ) : (
                              'Edit'
                            )}
                          </button>
                          {user.locked ? (
                            <button
                              onClick={() => handleUnlockUser(user.id)}
                              disabled={loadingOperations[`unlock_${user.id}`] || loadingOperations[`lock_${user.id}`]}
                              className="w-16 px-3 py-1.5 bg-green-500 text-white border-none rounded cursor-pointer text-xs font-medium hover:bg-green-600 transition-colors text-center disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center justify-center"
                            >
                              {loadingOperations[`unlock_${user.id}`] ? (
                                <svg className="animate-spin h-3 w-3" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                              ) : (
                                'Unlock'
                              )}
                            </button>
                          ) : (
                            <button
                              onClick={() => handleLockUser(user.id)}
                              disabled={loadingOperations[`lock_${user.id}`] || loadingOperations[`unlock_${user.id}`]}
                              className="w-16 px-3 py-1.5 bg-orange-500 text-white border-none rounded cursor-pointer text-xs font-medium hover:bg-orange-600 transition-colors text-center disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center justify-center"
                            >
                              {loadingOperations[`lock_${user.id}`] ? (
                                <svg className="animate-spin h-3 w-3" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                              ) : (
                                'Lock'
                              )}
                            </button>
                          )}
                          <button
                            onClick={() => handleDeleteUser(user.id)}
                            disabled={loadingOperations[`delete_${user.id}`]}
                            className="px-3 py-1.5 bg-red-600 text-white border-none rounded cursor-pointer text-xs font-medium hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center justify-center min-w-[60px]"
                          >
                            {loadingOperations[`delete_${user.id}`] ? (
                              <svg className="animate-spin h-3 w-3" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                              </svg>
                            ) : (
                              'Delete'
                            )}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {showCreateForm && (
        <CreateUserModal
          onClose={() => setShowCreateForm(false)}
          onSubmit={handleCreateUser}
          loading={loadingOperations.create}
        />
      )}

      {editingUser && (
        <EditUserModal
          user={editingUser}
          onClose={() => setEditingUser(null)}
          onSubmit={(userData) => handleUpdateUser(editingUser.id, userData)}
          loading={loadingOperations[`update_${editingUser.id}`]}
        />
      )}

      <ModalDialog
        open={confirmDialog.open}
        title={confirmDialog.title}
        description={confirmDialog.description}
        confirmLabel={confirmDialog.confirmLabel}
        cancelLabel="Cancel"
        variant={confirmDialog.variant}
        loading={confirmDialog.loading}
        onConfirm={confirmDialog.onConfirm}
        onClose={() =>
          setConfirmDialog(prev => ({
            ...prev,
            open: false,
          }))
        }
      />
    </div>
  );
}

export default AdminPanel;
